import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../../firebase/firebase';
import {
  collection,
  doc,
  setDoc,
  addDoc,
  serverTimestamp,
  query,
  orderBy,
  onSnapshot,
  getDoc,
  limit,
  updateDoc
} from 'firebase/firestore';
import { FiChevronLeft, FiMoreVertical, FiUser } from 'react-icons/fi';
import placeholderUser from '../../assets/images/images.jpeg';
import styles from './Chatroom.module.css';

const ChatRoom = () => {
  const { receiverId } = useParams();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatId, setChatId] = useState(null);
  const [chatMeta, setChatMeta] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimerRef = useRef(null);
  const currentUser = auth.currentUser;
  const navigate = useNavigate();

  // Add this function to send notifications
  const sendNotification = async (userId, type, fromUserId, fromUserName) => {
    try {
      await addDoc(collection(db, 'notifications'), {
        userId,
        type,
        fromUserId,
        fromUserName,
        read: false,
        timestamp: serverTimestamp()
      });
    } catch (error) {
      console.error("Error sending notification:", error);
    }
  };

  useEffect(() => {
    if (!currentUser || !receiverId) return;

    const fetchOtherUser = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', receiverId));
        if (userDoc.exists()) {
          setOtherUser({
            id: receiverId,
            name: userDoc.data().name || userDoc.data().displayName || 'User',
            profilePhoto: userDoc.data().profilePhoto || placeholderUser,
            online: Boolean(userDoc.data().online)
          });
        } else {
          setOtherUser({
            id: receiverId,
            name: 'User',
            profilePhoto: placeholderUser
          });
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setOtherUser({
          id: receiverId,
          name: 'User',
          profilePhoto: placeholderUser
        });
      }
    };

    fetchOtherUser();

    const ids = [currentUser.uid, receiverId].sort();
    const generatedChatId = ids.join('_');
    setChatId(generatedChatId);

    const chatRef = doc(db, 'chats', generatedChatId);
    setDoc(chatRef, {
      participants: ids,
      participantMeta: {
        [currentUser.uid]: {
          name: currentUser.displayName || currentUser.email || 'User',
          photo: currentUser.photoURL || ''
        },
      },
      lastMessage: '',
      lastSender: '',
      [`lastSeen.${currentUser.uid}`]: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });
  }, [currentUser, receiverId]);

  useEffect(() => {
    if (!chatId) return;

    const chatRef = doc(db, 'chats', chatId);
    const unsubscribeChat = onSnapshot(chatRef, (snapshot) => {
      setChatMeta(snapshot.exists() ? snapshot.data() : null);
    });

    const messagesRef = collection(db, 'chats', chatId, 'messages');
    const messagesQuery = query(
      messagesRef,
      orderBy('createdAt', 'asc'),
      limit(80)
    );

    const unsubscribeMessages = onSnapshot(messagesQuery, (snapshot) => {
      const messagesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(messagesData);

      updateDoc(chatRef, {
        [`lastSeen.${currentUser.uid}`]: serverTimestamp(),
        [`typing.${currentUser.uid}`]: false
      }).catch((error) => console.error('Error marking chat read:', error));
    });

    return () => {
      unsubscribeChat();
      unsubscribeMessages();
    };
  }, [chatId, currentUser?.uid]);

  useEffect(() => {
    if (!currentUser) return;

    const userRef = doc(db, 'users', currentUser.uid);
    updateDoc(userRef, {
      online: true,
      lastActiveAt: serverTimestamp()
    }).catch(() => {});

    const markOffline = () => {
      updateDoc(userRef, {
        online: false,
        lastActiveAt: serverTimestamp()
      }).catch(() => {});
    };

    window.addEventListener('beforeunload', markOffline);
    return () => {
      window.removeEventListener('beforeunload', markOffline);
      markOffline();
    };
  }, [currentUser]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const text = newMessage.trim().slice(0, 1000);
    if (!text || !chatId || !currentUser) return;

    try {
      const messagesRef = collection(db, 'chats', chatId, 'messages');
      await addDoc(messagesRef, {
        text,
        senderId: currentUser.uid,
        status: 'sent',
        createdAt: serverTimestamp()
      });

      const chatRef = doc(db, 'chats', chatId);
      await setDoc(chatRef, {
        lastMessage: text,
        lastSender: currentUser.uid,
        [`typing.${currentUser.uid}`]: false,
        updatedAt: serverTimestamp()
      }, { merge: true });

      // Send notification to the receiver
      await sendNotification(
        receiverId,
        'message',
        currentUser.uid,
        currentUser.displayName || currentUser.email
      );

      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  // ... rest of your component remains the same ...
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      handleSendMessage(e);
    }
  };

  const handleMessageInput = (event) => {
    const value = event.target.value.slice(0, 1000);
    setNewMessage(value);

    if (!chatId || !currentUser) return;

    const chatRef = doc(db, 'chats', chatId);
    updateDoc(chatRef, {
      [`typing.${currentUser.uid}`]: Boolean(value.trim())
    }).catch(() => {});

    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      updateDoc(chatRef, {
        [`typing.${currentUser.uid}`]: false
      }).catch(() => {});
    }, 1200);
  };

  const getImageKitUrl = (url) => {
    if (!url) return placeholderUser;
    if (url.startsWith('http') || url.startsWith('data:image')) return url;
    return `${process.env.REACT_APP_IMAGEKIT_URL_ENDPOINT}${url.startsWith('/') ? url : `/${url}`}`;
  };


  return (
    <div className={styles.chatRoomContainer}>
      <div className={styles.chatHeader}>
        <button className={styles.backButton} onClick={() => navigate('/messages')}>
          <FiChevronLeft />
        </button>
       
        <div className={styles.userInfo} onClick={() => navigate(`/profile/${otherUser?.id}`)}>
          <img
            src={getImageKitUrl(otherUser?.profilePhoto)}
            alt={otherUser?.name}
            className={styles.userAvatar}
            onError={(e) => {
              e.target.src = placeholderUser;
            }}
          />
          <div className={styles.userDetails}>
            <h3>{otherUser?.name}</h3>
            <p className={styles.status}>{otherUser?.online ? 'Online' : 'Offline'}</p>
          </div>
        </div>
       
        <div className={styles.chatActions}>
          <button className={styles.moreButton} onClick={() => setShowOptions(!showOptions)}>
            <FiMoreVertical />
          </button>
         
          {showOptions && (
            <div className={styles.optionsDropdown}>
              <button onClick={() => navigate(`/profile/${otherUser?.id}`)}>
                <FiUser /> View Profile
              </button>
            </div>
          )}
        </div>
      </div>
     
      <div className={styles.messagesContainer}>
        {messages.length === 0 ? (
          <p className={styles.noMessages}>
            No messages yet. Start the conversation with {otherUser?.name}!
          </p>
        ) : (
          messages.map(message => (
            <div
              key={message.id}
              className={`${styles.message} ${message.senderId === currentUser?.uid ? styles.sent : styles.received}`}
            >
              {message.senderId !== currentUser?.uid && (
                <img
                  src={getImageKitUrl(otherUser?.profilePhoto)}
                  alt={otherUser?.name}
                  className={styles.messageAvatar}
                  onError={(e) => {
                    e.target.src = placeholderUser;
                  }}
                />
              )}
              <div className={styles.messageContent}>
                <p>{message.text}</p>
                <span className={styles.messageTime}>
                  {message.createdAt?.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  {message.senderId === currentUser?.uid && (
                    <span className={styles.messageStatus}>
                      {chatMeta?.lastSeen?.[receiverId]?.toDate && message.createdAt?.toDate && chatMeta.lastSeen[receiverId].toDate() >= message.createdAt.toDate() ? 'Read' : 'Sent'}
                    </span>
                  )}
                </span>
              </div>
            </div>
          ))
        )}
        {chatMeta?.typing?.[receiverId] && (
          <div className={styles.typingIndicator}>{otherUser?.name || 'User'} is typing...</div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSendMessage} className={styles.messageForm}>
        <input
          type="text"
          value={newMessage}
          onChange={handleMessageInput}
          onKeyPress={handleKeyPress}
          placeholder={`Message ${otherUser?.name || 'User'}...`}
          maxLength={1000}
        />
        <button type="submit" disabled={!newMessage.trim()}>
          Send
        </button>
      </form>
    </div>
  );
};

export default ChatRoom;
